import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { PrivateFileIconLink } from '../components/PrivateFileAccess';
import {
  ArrowLeft, CheckCircle, XCircle, Eye, RefreshCw, Search,
  FileText, Shield, User, Mail, MapPin, Briefcase, Clock,
  AlertTriangle, RotateCcw, ExternalLink, Edit, CreditCard,
  Users, BadgeCheck
} from 'lucide-react';

const ROLE_TABS = ['all', 'professional', 'client'];
const VERIF_STATUSES = ['all', 'incomplete', 'submitted', 'approved', 'changes_requested', 'rejected'];

const subStatusConfig = {
  active: { label: 'Active', color: 'bg-green-100 text-green-800' },
  inactive: { label: 'Inactive', color: 'bg-gray-100 text-gray-600' },
  trial: { label: 'Trial', color: 'bg-yellow-100 text-yellow-800' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' },
};

const verifStatusConfig = {
  approved: { label: 'Verified', color: 'bg-green-100 text-green-800' },
  incomplete: { label: 'Incomplete', color: 'bg-yellow-100 text-yellow-800' },
  submitted: { label: 'Pending Review', color: 'bg-blue-100 text-blue-800' },
  changes_requested: { label: 'Corrections Required', color: 'bg-orange-100 text-orange-800' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
  'n/a': { label: 'N/A', color: 'bg-gray-100 text-gray-500' },
};

const VerificationManagement = () => {
  const navigate = useNavigate();
  const { user, API } = useContext(AuthContext);

  const [allUsers, setAllUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleTab, setRoleTab] = useState('all');
  const [verifFilter, setVerifFilter] = useState('all');
  const [search, setSearch] = useState('');

  // Verify modal (professionals)
  const [selectedPro, setSelectedPro] = useState(null);
  const [showVerifDialog, setShowVerifDialog] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  // Override modal (all users)
  const [selectedUser, setSelectedUser] = useState(null);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [overrideForm, setOverrideForm] = useState({
    subscription_status: '',
    subscription_ends_at: '',
    subscription_plan: '',
    verification_status: '',
    is_verified: false,
    is_admin: false,
  });
  const [overriding, setOverriding] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) { navigate('/'); return; }
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/verifications`, {
        params: { status_filter: 'all' }
      });
      setAllUsers(res.data || []);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = useCallback((data, role, verifSt, q) => {
    let list = data;
    if (role !== 'all') list = list.filter(u => u.role === role);
    if (verifSt !== 'all') {
      list = list.filter(u => {
        const vStatus = u.verification?.status || 'n/a';
        return vStatus === verifSt;
      });
    }
    if (q.trim()) {
      const lq = q.toLowerCase();
      list = list.filter(u =>
        u.user?.name?.toLowerCase().includes(lq) ||
        u.user?.email?.toLowerCase().includes(lq)
      );
    }
    setFiltered(list);
  }, []);

  useEffect(() => {
    applyFilter(allUsers, roleTab, verifFilter, search);
  }, [roleTab, verifFilter, search, allUsers]);

  // Stats
  const stats = {
    total: allUsers.length,
    professionals: allUsers.filter(u => u.role === 'professional').length,
    clients: allUsers.filter(u => u.role === 'client').length,
    active_subs: allUsers.filter(u => u.subscription_status === 'active').length,
    pending_verif: allUsers.filter(u => u.verification?.status === 'submitted').length,
  };

  // -- Verification actions --
  const openVerifDetails = (u) => {
    setSelectedPro(u);
    setAdminNotes(u.verification?.decision_reason || '');
    setShowVerifDialog(true);
  };

  const handleApprove = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/verifications/${selectedPro.user_id}/approve`, { notes: adminNotes });
      toast.success('Verification approved!');
      setShowVerifDialog(false);
      fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail?.message || (detail?.missing_fields ? `Missing: ${detail.missing_fields.join(', ')}` : detail) || 'Failed to approve');
    } finally { setProcessing(false); }
  };

  const handleReject = async () => {
    if (!adminNotes.trim()) { toast.error('Please add a rejection reason'); return; }
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/verifications/${selectedPro.user_id}/reject`, { notes: adminNotes });
      toast.success('Verification rejected.');
      setShowVerifDialog(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject');
    } finally { setProcessing(false); }
  };

  const handleRequestChanges = async () => {
    if (!adminNotes.trim()) { toast.error('Please add the required corrections'); return; }
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/verifications/${selectedPro.user_id}/request-changes`, { notes: adminNotes });
      toast.success('Corrections requested.'); setShowVerifDialog(false); fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail?.message || detail || 'Failed to request corrections');
    } finally { setProcessing(false); }
  };

  // -- Override actions --
  const openOverride = (u) => {
    setSelectedUser(u);
    const endsAt = u.subscription_ends_at ? u.subscription_ends_at.split('T')[0] : '';
    setOverrideForm({
      subscription_status: u.subscription_status || 'inactive',
      subscription_ends_at: endsAt,
      subscription_plan: u.subscription_plan || 'yearly',
      verification_status: u.verification?.status || '',
      is_verified: u.is_verified || false,
      is_admin: u.user?.is_admin || false,
    });
    setShowOverrideDialog(true);
  };

  const handleOverrideSave = async () => {
    setOverriding(true);
    try {
      const payload = {
        is_admin: overrideForm.is_admin,
      };
      await axios.put(`${API}/admin/users/${selectedUser.user_id}/override`, payload);
      toast.success(`Override applied for ${selectedUser.user?.name}`);
      setShowOverrideDialog(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Override failed');
    } finally { setOverriding(false); }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return iso; }
  };

  const getSubBadge = (status) => {
    const cfg = subStatusConfig[status] || subStatusConfig.inactive;
    return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>;
  };

  const getVerifBadge = (status) => {
    const cfg = verifStatusConfig[status] || verifStatusConfig['n/a'];
    return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            <h1 className="text-lg font-bold">User & Verification Management</h1>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAll} className="ml-auto" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">

        {/* Stats cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: 'Total Users', value: stats.total, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Professionals', value: stats.professionals, icon: Briefcase, color: 'text-purple-600', bg: 'bg-purple-50' },
            { label: 'Clients', value: stats.clients, icon: User, color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Active Subs', value: stats.active_subs, icon: CreditCard, color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { label: 'Pending Verif', value: stats.pending_verif, icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50' },
          ].map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} className={`p-3 rounded-xl border border-gray-200 bg-white flex items-center gap-3`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4 h-4 ${color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Role tabs + verif filter */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex rounded-lg border border-gray-200 bg-white overflow-hidden">
            {ROLE_TABS.map(tab => (
              <button
                key={tab}
                onClick={() => setRoleTab(tab)}
                className={`px-4 py-2 text-sm font-medium transition-colors capitalize ${roleTab === tab ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                data-testid={`role-tab-${tab}`}
              >
                {tab === 'all' ? 'All Users' : tab === 'professional' ? 'Professionals' : 'Clients'}
              </button>
            ))}
          </div>
          {/* Verification filter (only relevant for professionals) */}
          {(roleTab === 'all' || roleTab === 'professional') && (
            <select
              value={verifFilter}
              onChange={e => setVerifFilter(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="verif-filter-select"
            >
              <option value="all">All Verification Statuses</option>
              {VERIF_STATUSES.filter(s => s !== 'all').map(s => (
                <option key={s} value={s}>{verifStatusConfig[s]?.label || s}</option>
              ))}
            </select>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="verif-search"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p>No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">User</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Role</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Subscription</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Verification</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Joined</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((u) => {
                    const v = u.verification || {};
                    const vStatus = v.status || 'n/a';
                    const isPro = Boolean(u.verification);
                    return (
                      <tr key={u.user_id} className="hover:bg-gray-50 transition-colors" data-testid={`user-row-${u.user_id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <User className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 flex items-center gap-1">
                                {u.user?.name || '—'}
                                {u.user?.is_admin && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">Admin</span>}
                              </p>
                              <p className="text-xs text-gray-500">{u.user?.email || '—'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${isPro ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            {getSubBadge(u.subscription_status)}
                            {u.subscription_ends_at && (
                              <p className="text-xs text-gray-400 mt-0.5">Exp: {formatDate(u.subscription_ends_at)}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {isPro ? getVerifBadge(vStatus) : <span className="text-xs text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          {formatDate(u.user?.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1.5">
                            {/* Override button */}
                            <button
                              title="Edit / Override"
                              onClick={() => openOverride(u)}
                              className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50"
                              data-testid={`override-btn-${u.user_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            {/* Verify button (professionals only) */}
                            {isPro && (
                              <button
                                title="View Verification Details"
                                onClick={() => openVerifDetails(u)}
                                className="p-1.5 rounded-lg text-purple-600 hover:bg-purple-50"
                                data-testid={`verif-btn-${u.user_id}`}
                              >
                                <Shield className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-400 text-center">Showing {filtered.length} of {allUsers.length} total users</p>
      </div>

      {/* Verification Detail Dialog (Professionals) */}
      <Dialog open={showVerifDialog} onOpenChange={setShowVerifDialog}>
        <DialogContent className="max-h-[90vh] w-[calc(100vw-1rem)] max-w-2xl overflow-x-hidden overflow-y-auto">
          {selectedPro && (() => {
            const v = selectedPro.verification || {};
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex min-w-0 flex-wrap items-center gap-2 break-words pr-6">
                    <Shield className="w-5 h-5 text-blue-600" />
                    Verification — {selectedPro.user?.name}
                  </DialogTitle>
                </DialogHeader>

                <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                  <div className="flex min-w-0 items-center gap-2 text-gray-600">
                    <Mail className="w-4 h-4" />
                    <span className="break-all">{selectedPro.user?.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="w-4 h-4" />
                    Submitted: {formatDate(v.submitted_at)}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Current Status:</span>
                  {getVerifBadge(v.status || 'n/a')}
                  {v.verified_at && <span className="text-xs text-gray-500 ml-2">on {formatDate(v.verified_at)}</span>}
                </div>

                {/* Documents */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Documents
                  </h3>
                  <div className="space-y-2">
                    {[
                      { label: 'Profile Photo', url: v.profile_photo_url },
                      { label: 'ID Document', url: v.documents?.id_document_url },
                      { label: 'Business License', url: v.documents?.business_license_url },
                      { label: 'Insurance', url: v.documents?.insurance_url },
                    ].map(({ label, url }) => (
                      <div key={label} className={`flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg border ${url ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                        <div className="flex items-center gap-2">
                          {url ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-gray-400" />}
                          <span className="text-sm font-medium">{label}</span>
                        </div>
                        {url ? (
                          <PrivateFileIconLink reference={url} testId={`verification-file-${label.toLowerCase().replace(/\s+/g, '-')}`} />
                        ) : (
                          <span className="text-xs text-gray-400">Not submitted</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {v.decision_reason && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-xs font-semibold text-yellow-800 mb-1">Previous Admin Notes:</p>
                    <p className="text-sm text-yellow-700">{v.decision_reason}</p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1 block">
                    Review notes <span className="text-red-500">*required for corrections or rejection</span>
                  </label>
                  <Textarea
                    value={adminNotes}
                    onChange={e => setAdminNotes(e.target.value)}
                    placeholder="Add notes (required for rejection, optional for approval)..."
                    className="resize-none"
                    rows={3}
                    data-testid="admin-notes-input"
                  />
                </div>

                <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                  {v.status === 'submitted' && (
                    <Button
                      className="h-auto flex-1 whitespace-normal bg-green-600 py-3 hover:bg-green-700 text-white"
                      onClick={handleApprove}
                      disabled={processing || !v.complete}
                      data-testid="approve-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {processing ? 'Processing...' : 'Approve & Notify'}
                    </Button>
                  )}
                  {v.status === 'submitted' && (
                    <Button variant="outline" className="h-auto flex-1 whitespace-normal border-orange-300 py-3 text-orange-700 hover:bg-orange-50" onClick={handleRequestChanges} disabled={processing || !adminNotes.trim()} data-testid="request-changes-btn">
                      <AlertTriangle className="w-4 h-4 mr-2" />Request corrections
                    </Button>
                  )}
                  {v.status === 'submitted' && (
                    <Button
                      variant="outline"
                      className="h-auto flex-1 whitespace-normal border-red-200 py-3 text-red-600 hover:bg-red-50"
                      onClick={handleReject}
                      disabled={processing}
                      data-testid="reject-btn"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      {processing ? 'Processing...' : 'Reject & Notify'}
                    </Button>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Edit / Override Dialog */}
      <Dialog open={showOverrideDialog} onOpenChange={setShowOverrideDialog}>
        <DialogContent className="max-w-lg">
          {selectedUser && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Edit className="w-5 h-5 text-blue-600" />
                  Override — {selectedUser.user?.name}
                  <span className="text-xs text-gray-500 font-normal capitalize">({selectedUser.role})</span>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 py-2">
                <div className="border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900" data-testid="override-access-guidance">
                  Verification decisions use the review dialog. Time-limited access grants use the explicit yearly grant action so an expiry and granting administrator are recorded.
                </div>

                {/* Admin Role */}
                {selectedUser.user?.id !== user?.id && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div>
                      <p className="text-sm font-medium text-gray-800">Admin Role</p>
                      <p className="text-xs text-gray-500">Grant or revoke admin access</p>
                    </div>
                    <button
                      onClick={() => setOverrideForm(f => ({ ...f, is_admin: !f.is_admin }))}
                      className={`relative w-11 h-6 rounded-full transition-colors ${overrideForm.is_admin ? 'bg-purple-500' : 'bg-gray-300'}`}
                      data-testid="override-is-admin-toggle"
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${overrideForm.is_admin ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowOverrideDialog(false)}>
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={handleOverrideSave}
                  disabled={overriding}
                  data-testid="save-override-btn"
                >
                  {overriding ? 'Saving...' : 'Save Override'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VerificationManagement;
